import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BoltIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  MinusIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  KeyIcon,
  CreditCardIcon,
  UserCircleIcon,
  ClipboardDocumentIcon,
  PencilIcon,
  GiftIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../lib/apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserData {
  id: string;
  name: string;
  email: string;
  role: string;
  credits: number;
  email_verified_at: string | null;
  created_at: string;
  search_tasks_count: number;
}

interface UserStats {
  total_searches: number;
  completed_searches: number;
  failed_searches: number;
  credits_used: number;
  total_payments: number;
  total_leads_found: number;
}

interface SearchTask {
  id: string;
  company: string;
  type: string;
  status: string;
  progress: number;
  credits_used: number;
  created_at: string;
}

interface ApiKey {
  id: string;
  name: string;
  token_preview: string;
  total_requests: number;
  success_requests: number;
  last_used_at: string | null;
  created_at: string;
}

interface ApiStats {
  total_keys: number;
  total_requests: number;
}

interface Payment {
  id: string;
  amount: number;
  status: string;
  payment_method: string | null;
  credits_granted: number | null;
  created_at: string;
  plan?: { id: string; name: string } | null;
}

interface Paginated<T> {
  data: T[];
  total: number;
  current_page: number;
  per_page: number;
  last_page: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type TabId = 'overview' | 'api-keys' | 'payments' | 'plans';

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    completed:  'bg-emerald-50 text-emerald-700 border border-emerald-200',
    failed:     'bg-red-50 text-red-700 border border-red-200',
    processing: 'bg-brand-50 text-brand-700 border border-brand-200',
    pending:    'bg-slate-100 text-slate-500 border border-slate-200',
    retrieving: 'bg-blue-50 text-blue-700 border border-blue-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${map[status] ?? 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
      {status}
    </span>
  );
}

function SimplePaginator({ pagination, onPage }: {
  pagination: { current_page: number; last_page: number };
  onPage: (p: number) => void;
}) {
  if (pagination.last_page <= 1) return null;
  return (
    <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/40">
      <span className="text-[12px] text-slate-400">
        Page <span className="font-bold text-slate-700">{pagination.current_page}</span> of{' '}
        <span className="font-bold text-slate-700">{pagination.last_page}</span>
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPage(Math.max(1, pagination.current_page - 1))}
          disabled={pagination.current_page === 1}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </button>
        <button
          onClick={() => onPage(Math.min(pagination.last_page, pagination.current_page + 1))}
          disabled={pagination.current_page === pagination.last_page}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<UserData | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [tasks, setTasks] = useState<Paginated<SearchTask> | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [apiStats, setApiStats] = useState<ApiStats | null>(null);
  const [payments, setPayments] = useState<Paginated<Payment> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [tasksPage, setTasksPage] = useState(1);
  const [paymentsPage, setPaymentsPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [creditMode, setCreditMode] = useState<'add' | 'remove'>('add');
  const [creditAmount, setCreditAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState('');

  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<{ paymentId: string; planId: string } | null>(null);
  const [customPlanForm, setCustomPlanForm] = useState({ name: '', credits: '', amount: '' });
  const [planSubmitting, setPlanSubmitting] = useState(false);
  const [planFeedback, setPlanFeedback] = useState('');

  const [copied, setCopied] = useState<string | null>(null);
  const [customPlans, setCustomPlans] = useState<{ id: string; name: string; credits: number; price_monthly: number; created_at: string }[]>([]);

  useEffect(() => {
    if (!userId) return;
    apiClient.get(`/admin/users/${userId}/custom-plans`).then(({ data }) => setCustomPlans(data)).catch(() => {});
  }, [userId]);

  const fetchUser = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    apiClient.get(`/admin/users/${userId}`, {
      params: { tasks_page: tasksPage, payments_page: paymentsPage },
    })
      .then(({ data }) => {
        setUser(data.user);
        setStats(data.stats);
        setTasks(data.tasks);
        setApiKeys(data.api_keys ?? []);
        setApiStats(data.api_stats ?? null);
        setPayments(data.payments);
        setLoading(false);
      })
      .catch((err: any) => {
        const status = err?.response?.status;
        setError(status === 404 ? 'User not found.' : `Server error (${status ?? 'unknown'}) — check backend logs.`);
        setLoading(false);
      });
  }, [userId, tasksPage, paymentsPage]);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const openPlanModal = (plan?: { paymentId: string; planId: string; name: string; credits: number; amount: number }) => {
    if (plan) {
      setEditingPlan({ paymentId: plan.paymentId, planId: plan.planId });
      setCustomPlanForm({ name: plan.name, credits: String(plan.credits), amount: String(plan.amount) });
    } else {
      setEditingPlan(null);
      setCustomPlanForm({ name: '', credits: '', amount: '' });
    }
    setPlanFeedback('');
    setPlanModalOpen(true);
  };

  const handleCustomPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setPlanSubmitting(true);
    setPlanFeedback('');
    try {
      if (editingPlan) {
        await apiClient.put(`/admin/plans/${editingPlan.planId}`, {
          name: customPlanForm.name,
          credits: parseInt(customPlanForm.credits, 10),
          price_monthly: parseFloat(customPlanForm.amount) || 0,
        });
        setPlanFeedback('Plan updated.');
      } else {
        await apiClient.post(`/admin/users/${user.id}/custom-plan`, {
          name: customPlanForm.name,
          credits: parseInt(customPlanForm.credits, 10),
          amount: parseFloat(customPlanForm.amount) || 0,
        });
        setPlanFeedback('Custom plan created. User can now purchase it.');
      }
      setTimeout(() => {
        setPlanModalOpen(false);
        apiClient.get(`/admin/users/${user.id}/custom-plans`).then(({ data }) => setCustomPlans(data)).catch(() => {});
      }, 800);
    } catch (err: any) {
      setPlanFeedback(err?.response?.data?.message ?? 'Failed.');
    } finally {
      setPlanSubmitting(false);
    }
  };

  const handleDeletePlan = async (planId: string) => {
    if (!user) return;
    if (!window.confirm('Delete this custom plan? The user will no longer be able to purchase it.')) return;
    try {
      await apiClient.delete(`/admin/plans/${planId}`);
      setCustomPlans(prev => prev.filter(p => p.id !== planId));
    } catch (err: any) {
      alert(err?.response?.data?.message ?? 'Failed to delete plan.');
    }
  };

  const openModal = (mode: 'add' | 'remove') => {
    setCreditMode(mode);
    setCreditAmount('');
    setFeedback('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCreditAmount('');
    setFeedback('');
  };

  const handleCreditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    const amount = parseInt(creditAmount, 10);
    if (!amount || amount <= 0) return;
    setSubmitting(true);
    const delta = creditMode === 'remove' ? -amount : amount;
    try {
      const { data } = await apiClient.post(`/admin/users/${user.id}/adjust-credits`, { credits: delta });
      setUser(prev => prev ? { ...prev, credits: data.user.credits } : prev);
      setFeedback('Credits updated.');
      setTimeout(closeModal, 700);
    } catch (err: any) {
      setFeedback(err?.response?.data?.error ?? 'Failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Edit user (full details) ────────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: 'user', credits: '', password: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');

  const openEdit = () => {
    if (!user) return;
    setEditForm({ name: user.name, email: user.email, role: user.role || 'user', credits: String(user.credits ?? 0), password: '' });
    setEditError('');
    setEditOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setEditSubmitting(true);
    setEditError('');
    try {
      const payload: any = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        credits: parseInt(editForm.credits, 10) || 0,
      };
      if (editForm.password) payload.password = editForm.password;
      const { data } = await apiClient.put(`/admin/users/${user.id}`, payload);
      setUser(data.user);
      setEditOpen(false);
    } catch (err: any) {
      const resp = err?.response?.data;
      const firstErr = resp?.errors ? (Object.values(resp.errors)[0] as string[])?.[0] : undefined;
      setEditError(resp?.message || firstErr || 'Failed to update user.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const copyToken = (token: string, id: string) => {
    navigator.clipboard.writeText(token);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

  if (loading) {
    return (
      <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-[1200px] mx-auto">
        <div className="h-6 w-24 bg-slate-100 animate-pulse rounded mb-6" />
        <div className="bg-white border border-slate-200 rounded-2xl h-[140px] animate-pulse mb-5" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl h-[90px] animate-pulse" />
          ))}
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl h-64 animate-pulse" />
      </div>
    );
  }

  if (error || !user || !stats) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500 mb-1">{error ?? 'User not found.'}</p>
        <Link to="/users" className="text-brand-600 text-sm font-bold mt-2 inline-block">← Back to users</Link>
      </div>
    );
  }

  const TABS: { id: TabId; label: string; icon: React.ElementType; count?: number }[] = [
    { id: 'overview',  label: 'Overview',           icon: UserCircleIcon,       count: stats.total_searches },
    { id: 'api-keys',  label: 'API Keys',            icon: KeyIcon,              count: apiStats?.total_keys },
    { id: 'plans',     label: 'Plans',               icon: GiftIcon,             count: customPlans.length },
    { id: 'payments',  label: 'Credits & Payments',  icon: CreditCardIcon,       count: payments?.total },
  ];

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-[1200px] mx-auto flex flex-col gap-5 pb-20">

      {/* Back */}
      <button
        onClick={() => navigate('/users')}
        className="flex items-center gap-2 text-[12px] font-bold text-slate-500 hover:text-brand-600 transition-colors w-fit"
      >
        <ArrowLeftIcon className="w-3.5 h-3.5" /> Back to Users
      </button>

      {/* User Hero Card */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="w-14 h-14 bg-brand-500 rounded-2xl flex items-center justify-center shrink-0">
          <span className="text-[18px] font-black text-white">{getInitials(user.name)}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-[20px] font-extrabold text-slate-900">{user.name}</h1>
            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
              user.role === 'admin' ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-500'
            }`}>
              {user.role}
            </span>
            {user.email_verified_at ? (
              <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600">
                <CheckCircleIcon className="w-3.5 h-3.5" /> Verified
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[10px] font-bold text-slate-400">
                <XCircleIcon className="w-3.5 h-3.5" /> Unverified
              </span>
            )}
          </div>
          <p className="text-[13px] text-slate-400 mt-0.5">{user.email}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Joined {new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right border-r border-slate-200 pr-4">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Credit Balance</p>
            <p className="text-[28px] font-extrabold text-brand-600 leading-none">{user.credits.toLocaleString()}</p>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={openEdit}
              className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-colors"
            >
              <PencilIcon className="w-3.5 h-3.5" /> Edit
            </button>
            <button
              onClick={() => openPlanModal()}
              className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-colors"
            >
              <GiftIcon className="w-3.5 h-3.5" /> Assign Plan
            </button>
            <button
              onClick={() => openModal('add')}
              className="flex items-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-colors"
            >
              <PlusIcon className="w-3.5 h-3.5" /> Add
            </button>
            <button
              onClick={() => openModal('remove')}
              className="flex items-center gap-1.5 bg-white border border-red-200 hover:bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-colors"
            >
              <MinusIcon className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
        </div>
      </div>

      {/* Stat Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        {[
          { label: 'Total Searches',  value: stats.total_searches,     color: 'text-slate-900' },
          { label: 'Completed',       value: stats.completed_searches,  color: 'text-emerald-600' },
          { label: 'Failed',          value: stats.failed_searches,     color: 'text-red-600' },
          { label: 'Credits Used',    value: stats.credits_used,        color: 'text-amber-600' },
          { label: 'Leads Found',     value: stats.total_leads_found,   color: 'text-brand-600' },
          { label: 'Total Paid',      value: `$${stats.total_payments.toFixed(2)}`, color: 'text-emerald-600', raw: true },
        ].map(s => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1.5">{s.label}</p>
            <p className={`text-[22px] font-extrabold leading-none ${s.color}`}>
              {(s as any).raw ? s.value : (s.value as number).toLocaleString()}
            </p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-1">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-[13px] font-bold transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'text-brand-600 border-brand-600'
                  : 'text-slate-500 border-transparent hover:text-slate-800 hover:border-slate-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-500'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab: Overview ─────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400" />
            <p className="text-[13px] font-extrabold text-slate-700">Search History</p>
            {tasks && (
              <span className="ml-auto text-[11px] text-slate-400">{tasks.total} total</span>
            )}
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[560px]">
            <thead>
              <tr className="bg-slate-50/80 text-[10px] font-extrabold text-slate-400 uppercase tracking-[0.12em] border-b border-slate-100">
                <th className="px-5 py-3.5">Company</th>
                <th className="px-5 py-3.5">Type</th>
                <th className="px-5 py-3.5">Status</th>
                <th className="px-5 py-3.5">Progress</th>
                <th className="px-5 py-3.5">Credits</th>
                <th className="px-5 py-3.5">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {!tasks || tasks.data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-slate-400 text-[13px]">No searches yet.</td>
                </tr>
              ) : (
                tasks.data.map(task => (
                  <tr key={task.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-5 py-3.5 text-[13px] font-bold text-slate-900">{task.company}</td>
                    <td className="px-5 py-3.5 text-[12px] text-slate-500 capitalize">{task.type}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={task.status} /></td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${task.status === 'completed' ? 'bg-emerald-500' : task.status === 'failed' ? 'bg-red-400' : 'bg-brand-500'}`}
                            style={{ width: `${task.progress}%` }}
                          />
                        </div>
                        <span className="text-[11px] text-slate-400">{task.progress}%</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-[13px] font-bold text-slate-700">{task.credits_used}</td>
                    <td className="px-5 py-3.5 text-[11px] text-slate-500">
                      {new Date(task.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
          {tasks && <SimplePaginator pagination={tasks} onPage={setTasksPage} />}
        </div>
      )}

      {/* ── Tab: API Keys ──────────────────────────────────────────────────── */}
      {activeTab === 'api-keys' && (
        <div className="flex flex-col gap-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Total API Keys',   value: apiStats?.total_keys ?? 0,     color: 'text-slate-900' },
              { label: 'Total Requests',   value: apiStats?.total_requests ?? 0,  color: 'text-brand-600' },
            ].map(s => (
              <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{s.label}</p>
                <p className={`text-[28px] font-extrabold leading-none ${s.color}`}>{s.value.toLocaleString()}</p>
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <KeyIcon className="w-4 h-4 text-slate-400" />
              <p className="text-[13px] font-extrabold text-slate-700">API Keys</p>
              <span className="ml-auto text-[11px] text-slate-400">{apiKeys.length} key{apiKeys.length !== 1 ? 's' : ''}</span>
            </div>
            {apiKeys.length === 0 ? (
              <div className="px-5 py-12 text-center text-slate-400 text-[13px]">No API keys created yet.</div>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[640px]">
                <thead>
                  <tr className="bg-slate-50/80 text-[10px] font-extrabold text-slate-400 uppercase tracking-[0.12em] border-b border-slate-100">
                    <th className="px-5 py-3.5">Key Name</th>
                    <th className="px-5 py-3.5">Token</th>
                    <th className="px-5 py-3.5">Total Requests</th>
                    <th className="px-5 py-3.5">Successful</th>
                    <th className="px-5 py-3.5">Success Rate</th>
                    <th className="px-5 py-3.5">Last Used</th>
                    <th className="px-5 py-3.5">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {apiKeys.map(key => {
                    const rate = key.total_requests > 0
                      ? Math.round((key.success_requests / key.total_requests) * 100)
                      : null;
                    return (
                      <tr key={key.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-5 py-3.5 text-[13px] font-bold text-slate-900">{key.name}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <code className="text-[11px] font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                              {key.token_preview}
                            </code>
                            <button
                              onClick={() => copyToken(key.token_preview, key.id)}
                              className="text-slate-400 hover:text-slate-700 transition-colors"
                              title="Copy"
                            >
                              <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                            </button>
                            {copied === key.id && (
                              <span className="text-[10px] text-emerald-600 font-bold">Copied!</span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-[13px] font-bold text-slate-900">{key.total_requests.toLocaleString()}</td>
                        <td className="px-5 py-3.5 text-[13px] font-semibold text-emerald-600">{key.success_requests.toLocaleString()}</td>
                        <td className="px-5 py-3.5">
                          {rate !== null ? (
                            <div className="flex items-center gap-2">
                              <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${rate >= 90 ? 'bg-emerald-500' : rate >= 70 ? 'bg-amber-400' : 'bg-red-400'}`}
                                  style={{ width: `${rate}%` }}
                                />
                              </div>
                              <span className="text-[11px] font-bold text-slate-600">{rate}%</span>
                            </div>
                          ) : (
                            <span className="text-slate-300 text-[12px]">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-[11px] text-slate-500">
                          {key.last_used_at
                            ? new Date(key.last_used_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : <span className="text-slate-300">Never</span>}
                        </td>
                        <td className="px-5 py-3.5 text-[11px] text-slate-500">
                          {new Date(key.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Plans ────────────────────────────────────────────────────── */}
      {activeTab === 'plans' && (
        <div className="flex flex-col gap-4">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <GiftIcon className="w-4 h-4 text-slate-400" />
                <p className="text-[13px] font-extrabold text-slate-700">Plans Assigned</p>
              </div>
              <button
                onClick={() => openPlanModal()}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-colors"
              >
                <PlusIcon className="w-3.5 h-3.5" /> Custom Plan
              </button>
            </div>
            {customPlans.length === 0 ? (
              <div className="px-5 py-12 text-center text-slate-400 text-[13px]">No custom plans created yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left min-w-[560px]">
                  <thead>
                    <tr className="bg-slate-50/80 text-[10px] font-extrabold text-slate-400 uppercase tracking-[0.12em] border-b border-slate-100">
                      <th className="px-5 py-3.5">Plan Name</th>
                      <th className="px-5 py-3.5">Credits</th>
                      <th className="px-5 py-3.5">Price</th>
                      <th className="px-5 py-3.5">Created</th>
                      <th className="px-5 py-3.5"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {customPlans.map(p => (
                      <tr key={p.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-5 py-3.5 text-[13px] font-bold text-slate-900">{p.name}</td>
                        <td className="px-5 py-3.5 text-[13px] font-extrabold text-brand-600">{p.credits.toLocaleString()}</td>
                        <td className="px-5 py-3.5 text-[14px] font-extrabold text-emerald-600">
                          {Number(p.price_monthly) === 0 ? <span className="text-slate-400 font-semibold text-[12px]">Free</span> : `$${Number(p.price_monthly).toFixed(2)}`}
                        </td>
                        <td className="px-5 py-3.5 text-[11px] text-slate-500">
                          {new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openPlanModal({ paymentId: '', planId: p.id, name: p.name, credits: p.credits, amount: Number(p.price_monthly) })}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                              title="Edit plan"
                            >
                              <PencilIcon className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeletePlan(p.id)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Delete plan"
                            >
                              <XMarkIcon className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Credits & Payments ────────────────────────────────────────── */}
      {activeTab === 'payments' && (
        <div className="flex flex-col gap-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { label: 'Total Paid',      value: `$${stats.total_payments.toFixed(2)}`, color: 'text-emerald-600', raw: true },
              { label: 'Credits Balance', value: user.credits,                          color: 'text-brand-600' },
              { label: 'Credits Used',    value: stats.credits_used,                    color: 'text-amber-600' },
            ].map(s => (
              <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{s.label}</p>
                <p className={`text-[28px] font-extrabold leading-none ${s.color}`}>
                  {(s as any).raw ? s.value : (s.value as number).toLocaleString()}
                </p>
              </div>
            ))}
          </div>

          {/* Quick credit adjustment */}
          <div className="bg-white border border-slate-200 rounded-2xl px-5 py-4 flex items-center gap-4 shadow-sm">
            <BoltIcon className="w-4 h-4 text-brand-500 shrink-0" />
            <span className="text-[13px] font-bold text-slate-700 mr-auto">
              Balance: <span className="text-brand-600 font-extrabold">{user.credits.toLocaleString()} credits</span>
            </span>
            <button
              onClick={() => openModal('add')}
              className="flex items-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white px-3.5 py-2 rounded-xl text-[12px] font-bold transition-colors"
            >
              <PlusIcon className="w-3.5 h-3.5" /> Add Credits
            </button>
            <button
              onClick={() => openModal('remove')}
              className="flex items-center gap-1.5 bg-white border border-red-200 hover:bg-red-50 text-red-600 px-3.5 py-2 rounded-xl text-[12px] font-bold transition-colors"
            >
              <MinusIcon className="w-3.5 h-3.5" /> Remove Credits
            </button>
          </div>

          {/* Payments table */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <CreditCardIcon className="w-4 h-4 text-slate-400" />
              <p className="text-[13px] font-extrabold text-slate-700">Payment History</p>
              {payments && (
                <span className="ml-auto text-[11px] text-slate-400">{payments.total} transaction{payments.total !== 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[480px]">
              <thead>
                <tr className="bg-slate-50/80 text-[10px] font-extrabold text-slate-400 uppercase tracking-[0.12em] border-b border-slate-100">
                  <th className="px-5 py-3.5">Plan</th>
                  <th className="px-5 py-3.5">Amount</th>
                  <th className="px-5 py-3.5">Method</th>
                  <th className="px-5 py-3.5">Status</th>
                  <th className="px-5 py-3.5">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {!payments || payments.data.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-slate-400 text-[13px]">No payment history.</td>
                  </tr>
                ) : (
                  payments.data.map(p => (
                    <tr key={p.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-3.5 text-[13px] font-semibold text-slate-700">
                        {p.plan?.name ?? <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-5 py-3.5 text-[14px] font-extrabold text-emerald-600">
                        ${Number(p.amount).toFixed(2)}
                      </td>
                      <td className="px-5 py-3.5 text-[12px] text-slate-500 capitalize">
                        {p.payment_method ?? '—'}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                          p.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : p.status === 'failed'   ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-[11px] text-slate-500">
                        {new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
            {payments && <SimplePaginator pagination={payments} onPage={setPaymentsPage} />}
          </div>
        </div>
      )}

      {/* Credit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="text-[14px] font-extrabold text-slate-900">
                {creditMode === 'add' ? 'Add Credits' : 'Remove Credits'}
              </h2>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-900 transition-colors">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-5">
              <p className="text-[12px] text-slate-500 mb-4">
                Current balance:{' '}
                <span className="font-extrabold text-slate-900">{user.credits.toLocaleString()} credits</span>
              </p>
              <form onSubmit={handleCreditSubmit} className="flex flex-col gap-4">
                <div>
                  <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Amount</label>
                  <input
                    type="number"
                    min="1"
                    value={creditAmount}
                    onChange={e => setCreditAmount(e.target.value)}
                    placeholder="e.g. 100"
                    className="w-full px-3 py-3 border border-slate-200 rounded-xl text-[14px] font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-colors"
                    required
                    autoFocus
                  />
                </div>
                {feedback && (
                  <p className={`text-[12px] font-semibold ${feedback.includes('updated') ? 'text-emerald-600' : 'text-red-600'}`}>
                    {feedback}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full py-3 rounded-xl text-[12px] font-black uppercase tracking-widest text-white transition-colors disabled:opacity-60 ${
                    creditMode === 'add' ? 'bg-brand-500 hover:bg-brand-600' : 'bg-red-500 hover:bg-red-600'
                  }`}
                >
                  {submitting ? 'Updating…' : `Confirm ${creditMode === 'add' ? '+' : '−'}${creditAmount || '0'}`}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {planModalOpen && user && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="text-[14px] font-extrabold text-slate-900">{editingPlan ? 'Edit Plan' : 'Custom Plan'}</h2>
              <button onClick={() => setPlanModalOpen(false)} className="text-slate-400 hover:text-slate-900 transition-colors">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-5">
              <p className="text-[12px] text-slate-500 mb-4">
                Create a one-off plan for <span className="font-extrabold text-slate-900">{user.name}</span>. Credits will be added to their balance immediately.
              </p>
              <form onSubmit={handleCustomPlan} className="flex flex-col gap-4">
                <div>
                  <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Plan Name</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Special Deal"
                    value={customPlanForm.name}
                    onChange={e => setCustomPlanForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-3 border border-slate-200 rounded-xl text-[14px] font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-colors"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Credits</label>
                    <input
                      type="number"
                      required
                      min="1"
                      placeholder="e.g. 5000"
                      value={customPlanForm.credits}
                      onChange={e => setCustomPlanForm(f => ({ ...f, credits: e.target.value }))}
                      className="w-full px-3 py-3 border border-slate-200 rounded-xl text-[14px] font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Amount ($)</label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      placeholder="e.g. 2000"
                      value={customPlanForm.amount}
                      onChange={e => setCustomPlanForm(f => ({ ...f, amount: e.target.value }))}
                      className="w-full px-3 py-3 border border-slate-200 rounded-xl text-[14px] font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-colors"
                    />
                  </div>
                </div>
                {planFeedback && (
                  <p className={`text-[12px] font-semibold ${planFeedback.includes('created') ? 'text-emerald-600' : 'text-red-600'}`}>
                    {planFeedback}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={planSubmitting || !customPlanForm.name || !customPlanForm.credits}
                  className="w-full py-3 rounded-xl text-[12px] font-black uppercase tracking-widest text-white bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-60"
                >
                  {planSubmitting ? (editingPlan ? 'Saving…' : 'Creating…') : (editingPlan ? 'Save Changes' : 'Create & Assign')}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {editOpen && user && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setEditOpen(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-extrabold text-slate-900">Edit User</h3>
              <button onClick={() => setEditOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100"><XMarkIcon className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleEditSubmit} className="flex flex-col gap-4">
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Full Name</label>
                <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
              </div>
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Email</label>
                <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Role</label>
                  <select value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-[13px] text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20">
                    <option value="user">User</option>
                    <option value="support">Support</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Credits</label>
                  <input type="number" min="0" value={editForm.credits} onChange={e => setEditForm(f => ({ ...f, credits: e.target.value }))} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">New Password <span className="text-slate-300 font-medium normal-case">(leave blank to keep)</span></label>
                <input type="password" value={editForm.password} onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
              </div>
              {editError && <p className="text-[12px] text-red-500 font-semibold">{editError}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setEditOpen(false)} className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 font-bold rounded-xl text-[13px] hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={editSubmitting} className="flex-1 px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white font-bold rounded-xl text-[13px]">{editSubmitting ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
